import { createContext, useContext } from "react";
import type { Transaction, VersionedTransaction } from "@solana/web3.js";
import type { PublicClient } from "viem";
import type { PublicInvoice, SolanaPrep } from "../lib/types";

/**
 * Transaction builders, injected so the checkout does not own chain logic.
 *
 * Both run in the payer's browser: they build an unsigned transaction that the payer's own wallet
 * signs and sends. Nothing here signs, and the server never builds, signs, relays or submits
 * (docs/CONTRACT.md, hard rules 1 and 2).
 *
 * The real implementations are in `realBuilders.ts` (`createPayBuilders`): Solana through
 * `@mikodes-checkout/solana`, Base through a viem calldata builder for MikodesPayRouter.pay (plus the
 * ERC-20 approve when the allowance is short). `unwiredPayBuilders` is only what the development mock
 * build (`VITE_MOCK=1`) injects.
 */
export interface SolanaPayBuilder {
  buildWalletPayTransaction(input: {
    invoice: PublicInvoice;
    /** Payer wallet public key, base58. */
    payer: string;
    prep: SolanaPrep;
  }): Promise<Transaction | VersionedTransaction>;
}

export interface EvmCall {
  /** "approve" is the ERC-20 allowance step; "pay" is MikodesPayRouter.pay. */
  kind: "approve" | "pay";
  to: `0x${string}`;
  data: `0x${string}`;
  value?: bigint;
}

export interface EvmPayBuilder {
  /** Ordered calls the payer's wallet sends; each waits for the previous receipt. */
  buildPayCalls(input: {
    invoice: PublicInvoice;
    payer: `0x${string}`;
    publicClient: PublicClient;
  }): Promise<EvmCall[]>;
}

export interface PayBuilders {
  solana: SolanaPayBuilder | null;
  evm: EvmPayBuilder | null;
}

/** Until the chain packages are wired, wallet pay shows a plain "not available" state. */
export const unwiredPayBuilders: PayBuilders = { solana: null, evm: null };

export const PayBuildersContext = createContext<PayBuilders>(unwiredPayBuilders);

export function usePayBuilders(): PayBuilders {
  return useContext(PayBuildersContext);
}
